import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Globe, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import PageWrapper from "@/components/PageWrapper";
import Particles from "@/components/Particles";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const BUDGETS = [
  "Under $2,000",
  "$2,000 – $5,000",
  "$5,000 – $10,000",
  "$10,000 – $25,000",
  "$25,000+",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    budget: "",
    project_details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_details) {
      toast.error("Please fill in name, email and project details.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        budget: "",
        project_details: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh" aria-hidden />
        <Particles count={26} />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28 pb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
            Contact
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-5 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-white max-w-5xl"
          >
            Let&apos;s Discuss <br className="hidden md:block" />
            Your <span className="text-gradient-accent">Project.</span>
          </motion.h1>
          <p className="mt-7 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed">
            Tell us where you are and where you want to go. We&apos;ll respond
            within 24 hours with thoughtful next steps.
          </p>
        </div>
      </section>

      <section className="relative pb-24 md:pb-32 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 pt-16 md:pt-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* CONTACT INFO */}
          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-7">
              <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
                Email us
              </p>
              <a
                data-testid="contact-email-link"
                href="mailto:info@codingsolutions.in"
                className="mt-3 inline-flex items-center gap-2 font-display text-2xl text-white hover:text-blue-300 transition-colors"
              >
                <Mail size={18} /> info@codingsolutions.in
              </a>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-7">
              <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
                Website
              </p>
              <a
                data-testid="contact-website-link"
                href="https://codingsolutions.in"
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 inline-flex items-center gap-2 font-display text-2xl text-white hover:text-blue-300 transition-colors"
              >
                <Globe size={18} /> codingsolutions.in
              </a>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md overflow-hidden">
              <div className="aspect-[16/10] relative">
                <div className="absolute inset-0 gradient-mesh" />
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-center px-6">
                    <MapPin className="mx-auto text-blue-400" size={22} />
                    <p className="mt-3 font-display text-xl text-white">
                      Remote-first studio
                    </p>
                    <p className="text-sm text-zinc-400 mt-1">
                      Serving clients in 10+ countries.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-8">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-7 md:p-10"
            >
              {done ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <CheckCircle2 className="mx-auto text-blue-400" size={36} />
                  <h3 className="mt-5 font-display text-3xl text-white">
                    Message received.
                  </h3>
                  <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                    Thanks for reaching out — we&apos;ll get back to you within
                    24 hours.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-send-another"
                    onClick={() => setDone(false)}
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white text-sm px-5 py-3"
                  >
                    Send another <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field
                      label="Name"
                      required
                      testId="contact-input-name"
                      value={form.name}
                      onChange={(v) => update("name", v)}
                    />
                    <Field
                      label="Email"
                      type="email"
                      required
                      testId="contact-input-email"
                      value={form.email}
                      onChange={(v) => update("email", v)}
                    />
                    <Field
                      label="Phone"
                      testId="contact-input-phone"
                      value={form.phone}
                      onChange={(v) => update("phone", v)}
                    />
                    <Field
                      label="Company"
                      testId="contact-input-company"
                      value={form.company}
                      onChange={(v) => update("company", v)}
                    />
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                        Budget
                      </label>
                      <Select
                        value={form.budget}
                        onValueChange={(v) => update("budget", v)}
                      >
                        <SelectTrigger
                          data-testid="contact-input-budget"
                          className="mt-2 bg-transparent border-white/15 text-white h-12 rounded-xl"
                        >
                          <SelectValue placeholder="Select a budget range" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                          {BUDGETS.map((b) => (
                            <SelectItem
                              key={b}
                              value={b}
                              data-testid={`contact-budget-option-${b
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")}`}
                            >
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                        Project Details *
                      </label>
                      <Textarea
                        data-testid="contact-input-project-details"
                        value={form.project_details}
                        onChange={(e) =>
                          update("project_details", e.target.value)
                        }
                        rows={6}
                        placeholder="Tell us about your business, goals and timeline…"
                        className="mt-2 bg-transparent border-white/15 text-white rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-xs text-zinc-500">
                      We&apos;ll never share your details. Reply within 24
                      hours.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white text-sm font-medium px-6 py-3 transition-colors shadow-[0_10px_30px_-10px_rgba(59,130,246,0.7)]"
                    >
                      {submitting ? "Sending…" : "Send Message"}
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}

function Field({ label, type = "text", value, onChange, testId, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
        {label}
        {required && " *"}
      </label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 bg-transparent border-white/15 text-white h-12 rounded-xl"
      />
    </div>
  );
}
